import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';
import {Panel, PanelGroup, PanelResizeHandle} from 'react-resizable-panels';

import {ChatContext} from 'llm/chat-library/ChatContext';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {GRAY, GRAY_LIGHT, WHITE, hexToRgba} from 'ui-library/Colors';
import useOnMount from 'ui-library/hooks/useOnMount';

import type {ChatLayoutProps} from '../ChatLayoutRegistry';
import {TabbedViewerPane} from '../TabbedViewerPane';
import {useTabbedViewer} from '../useTabbedViewer';

/**
 * Workspace layout for Analysis chat.
 *
 * Shows: [Plan pane] | [Chat pane] | [Tabbed Viewer pane]
 *
 * - Plan pane: hidden until plan artifact appears from application state
 * - Chat pane: standard chat content
 * - Viewer pane: hidden until user clicks a file; supports multiple file tabs
 *
 */
export function AnalysisWorkspaceLayout({chatContent, artifacts, isEditable}: ChatLayoutProps): JSX.Element {
  const {planArtifact} = artifacts;
  const [isPlanResizerDragging, setIsPlanResizerDragging] = useState(false);
  const [isViewerResizerDragging, setIsViewerResizerDragging] = useState(false);
  const {tabs, activeTab, openTab, closeTab, setActiveTab} = useTabbedViewer();

  useOnMount(() => {
    SidepanelActions.expand();
  });

  const openFile = useCallback(
    (chatSessionId: string, externalFileId: string, filename: string, mimeType: string) => {
      openTab({
        kind: 'file',
        id: externalFileId,
        chatSessionId,
        externalFileId,
        filename,
        mimeType,
        label: filename,
      });
    },
    [openTab]
  );

  const contextValue = useMemo(() => ({openFile}), [openFile]);

  const hasPlan = planArtifact != null;
  const hasViewer = tabs.length > 0;

  // Panel sizes must sum to 100 for visible panels
  let chatSize: number;
  let planSize: number;
  let viewerSize: number;
  if (hasPlan && hasViewer) {
    planSize = 15;
    chatSize = 50;
    viewerSize = 35;
  } else if (hasPlan) {
    planSize = 20;
    chatSize = 80;
    viewerSize = 0;
  } else if (hasViewer) {
    planSize = 0;
    chatSize = 60;
    viewerSize = 40;
  } else {
    planSize = 0;
    chatSize = 100;
    viewerSize = 0;
  }

  return (
    <ChatContext.Provider value={contextValue}>
      <div className={css(styles.container)} aria-label="Analysis workspace">
        <PanelGroup direction="horizontal" style={{width: '100%', height: '100%'}}>
          {/* Plan panel */}
          {planArtifact && (
            <>
              <Panel
                id="plan"
                order={0}
                defaultSize={planSize}
                minSize={10}
                maxSize={35}
                className={css(styles.panel)}
              >
                <div role="region" aria-label="Plan panel" className={css(styles.panelWrapper)}>
                  <div className={css(styles.paneHeader)}>Plan</div>
                  <div className={css(styles.scrollableContent)}>
                    <planArtifact.Component data={planArtifact.data} editable={isEditable} />
                  </div>
                </div>
              </Panel>
              <PanelResizeHandle
                className={css(styles.resizer, isPlanResizerDragging && styles.resizerActive)}
                onDragging={setIsPlanResizerDragging}
              >
                <div aria-hidden="true" className={css(styles.resizerWrapper)}>
                  <div className={css(styles.resizerHandle)} />
                </div>
              </PanelResizeHandle>
            </>
          )}

          {/* Chat panel */}
          <Panel id="chat" order={1} defaultSize={chatSize} minSize={20} className={css(styles.panel)}>
            <div role="region" aria-label="Chat panel" className={css(styles.panelWrapper)}>
              <div className={css(styles.scrollableContent)}>{chatContent}</div>
            </div>
          </Panel>

          {/* Viewer panel */}
          {hasViewer && (
            <>
              <PanelResizeHandle
                className={css(styles.resizer, isViewerResizerDragging && styles.resizerActive)}
                onDragging={setIsViewerResizerDragging}
              >
                <div aria-hidden="true" className={css(styles.resizerWrapper)}>
                  <div className={css(styles.resizerHandle)} />
                </div>
              </PanelResizeHandle>
              <Panel
                id="viewer"
                order={2}
                defaultSize={viewerSize}
                minSize={15}
                maxSize={60}
                className={css(styles.panel)}
              >
                <div role="region" aria-label="Viewer panel" className={css(styles.panelWrapper)}>
                  <TabbedViewerPane
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    onTabClose={closeTab}
                  />
                </div>
              </Panel>
            </>
          )}
        </PanelGroup>
      </div>
    </ChatContext.Provider>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  panel: {
    height: '100%',
  },
  panelWrapper: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: WHITE,
  },
  paneHeader: {
    padding: '12px 16px',
    fontSize: 14,
    fontWeight: 600,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    flexShrink: 0,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  resizer: {
    width: '12px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    position: 'relative',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s ease',
    ':hover': {
      backgroundColor: `rgba(${hexToRgba(GRAY_LIGHT, 0.3)})`,
    },
    boxShadow: `2px 0 8px 0 ${GRAY}, inset -1px 0 0 0 ${GRAY_LIGHT}`,
  },
  resizerWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  resizerActive: {
    backgroundColor: `rgba(${hexToRgba(GRAY_LIGHT, 0.5)})`,
  },
  resizerHandle: {
    width: '2px',
    height: '40px',
    backgroundColor: GRAY_LIGHT,
    borderRadius: '2px',
    transition: 'all 0.15s ease',
  },
});
